import { isProduction } from '../utils/environmentUtils';
import { TestingEnvironment } from './types';

/**
 * Which shared-results bucket this frontend reads/writes.
 *
 * Anything that isn't production (uat, qa, dev/local stacks pointed at the UAT
 * backend) shares the 'uat' bucket — release QA happens on UAT and local dev
 * stacks are part of that same validation pass.
 */
export const getTestingEnvironment = (): TestingEnvironment =>
  isProduction() ? 'production' : 'uat';

/**
 * Custom roles allowed to see the Testing Center — Wally dev ONLY, in EVERY
 * environment (tightened 2026-07-16 per Walid; was everyone-on-UAT +
 * admins-in-production). It's an internal dev/QA tool, not a work surface, so
 * pharmacy staff shouldn't see test views at all.
 */
export const TESTING_CENTER_ROLES = ['wally_dev_admin'];

/**
 * Dedicated front-end feature flag gating the Testing Center (GP-1178).
 *
 * Fail-closed: the Testing Center is hidden unless `VITE_TESTING_CENTER_ENABLED`
 * is the exact string `"true"`. A missing or any-other-value env var defaults to
 * OFF, so no deploy ever exposes the internal QA tool by accident. This flag
 * COMPOSES WITH the `WALI DEV` role guard below — both must pass — rather than
 * replacing it. (The ticket named a CRA-style `REACT_APP_*` var, but this app is
 * Vite; the Vite `VITE_*` convention is what `import.meta.env` actually exposes.)
 */
export const isTestingCenterEnabled = (): boolean =>
  import.meta.env.VITE_TESTING_CENTER_ENABLED === 'true';

/**
 * The same flag, read ONCE at module load, for the lazy imports of the Testing
 * Center page and overlay panel (PRX-121).
 *
 * Vite replaces `import.meta.env.VITE_*` with a literal at build time, so in a
 * build where the flag is off this is the constant `false`, the
 * `React.lazy(() => import(...))` behind it is dead code, and Rollup never emits
 * the chunk that holds the guides, routeHints and generated/test-suites.json.
 * `isTestingCenterEnabled()` above stays the runtime visibility gate (tests stub
 * the env per case); use this constant only to decide whether the code exists.
 * `scripts/check-testing-center-excluded.mjs` fails the build if the chunk
 * comes back.
 */
export const TESTING_CENTER_BUILD_ENABLED: boolean =
  import.meta.env.VITE_TESTING_CENTER_ENABLED === 'true';

/**
 * Visibility gate shared by the sidebar entry and the page itself.
 *
 * Two gates compose here (GP-1178):
 *  1. The `VITE_TESTING_CENTER_ENABLED` env flag must be on — fail-closed, so a
 *     missing/false flag hides the Testing Center for EVERYONE, no exceptions.
 *  2. The `WALI DEV` role check: a user with NO custom role on file still sees it
 *     (repo-wide RBAC graceful-fallback, no lockout); every role-holding
 *     non-Wally-dev user is excluded.
 * @param customRoleLoaded the user's custom role object (null = none on file)
 * @param hasCustomRole    the useAuth() checker
 */
export const canSeeTestingCenter = (
  customRoleLoaded: unknown | null,
  hasCustomRole: (role: string) => boolean,
): boolean => {
  if (!isTestingCenterEnabled()) return false; // env flag off → hidden for all (fail-closed)
  if (!customRoleLoaded) return true; // null role → show ALL (graceful fallback)
  return TESTING_CENTER_ROLES.some((role) => hasCustomRole(role));
};
